import { warn } from './console';
import { getNumericTwsFromRow } from './boxPlotStripOrder';
import { resolveDataField } from './colorScale';

export const BOXPLOT_TWS_FIELD = '__boxplot_tws';

/**
 * Box plot pipeline: narrow aggregate rows to the global brush/scatter selection.
 * Scatter uses the full `base` slice; box plots use this helper so selection only affects summary stats.
 */
export function sliceAggregatesForBoxPlot<T extends { event_id?: number | string | null }>(
  base: readonly T[],
  selectedEventIds: readonly number[]
): T[] {
  if (!Array.isArray(base) || base.length === 0) return base ? [...base] : [];
  if (!Array.isArray(selectedEventIds) || selectedEventIds.length === 0) return [...base];

  const idSet = new Set<number>(
    selectedEventIds.filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
  );
  const sliced = base.filter((row) => row.event_id != null && idSet.has(Number(row.event_id)));

  if (sliced.length === 0 && base.length > 0) {
    warn(
      '[sliceAggregatesForBoxPlot] selection produced empty slice from non-empty base; treating selection as stale and keeping full base',
      {
        baseLen: base.length,
        selectionLen: selectedEventIds.length
      }
    );
    return [...base];
  }
  return sliced;
}

type EventRow = { event_id?: number | string | null } & Record<string, unknown>;

/**
 * Selection-sliced aggregate rows with a guaranteed per-event numeric TWS field for BoxPlot strip ordering.
 * Falls back to looking up TWS by `event_id` from `twsSourceRows` (typically AVG aggregates).
 */
export function sliceAggregatesForBoxPlotWithTws<T extends EventRow>(
  base: readonly T[],
  selectedEventIds: readonly number[],
  twsSourceRows?: readonly EventRow[],
  preferredTwsField?: string
): Array<T & { [BOXPLOT_TWS_FIELD]?: number }> {
  const sliced = sliceAggregatesForBoxPlot(base, selectedEventIds);
  if (!Array.isArray(sliced) || sliced.length === 0) return sliced as Array<T & { [BOXPLOT_TWS_FIELD]?: number }>;

  const sourceRows = Array.isArray(twsSourceRows) && twsSourceRows.length > 0 ? twsSourceRows : base;
  const twsByEventId = new Map<number, number>();
  const getSortValue = (row: EventRow): number | null => {
    if (preferredTwsField) {
      const preferred = resolveDataField(row, preferredTwsField);
      if (preferred !== undefined && preferred !== null && preferred !== '') {
        const n = Number(preferred);
        if (Number.isFinite(n)) return n;
      }
    }
    return getNumericTwsFromRow(row, { preferredTwsField });
  };

  sourceRows.forEach((row) => {
    const rawId = row.event_id;
    const eventId = rawId == null ? NaN : Number(rawId);
    if (!Number.isFinite(eventId) || twsByEventId.has(eventId)) return;
    const tws = getSortValue(row);
    if (tws !== null && Number.isFinite(tws)) {
      twsByEventId.set(eventId, tws);
    }
  });

  return sliced.map((row) => {
    const directTws = getSortValue(row);
    if (directTws !== null && Number.isFinite(directTws)) {
      return { ...row, [BOXPLOT_TWS_FIELD]: directTws };
    }

    const rawId = row.event_id;
    const eventId = rawId == null ? NaN : Number(rawId);
    if (Number.isFinite(eventId)) {
      const fallbackTws = twsByEventId.get(eventId);
      if (fallbackTws !== undefined) {
        return { ...row, [BOXPLOT_TWS_FIELD]: fallbackTws };
      }
    }
    return row;
  });
}
